import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
    Activity,
    ArrowRight,
    BellRing,
    ClipboardList,
    Database,
    Plus,
    Search,
    Trash2,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { AppShell } from '~/components/layout/app-shell'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { ErrorBanner } from '~/components/ui/error-banner'
import { Modal } from '~/components/ui/modal'
import { Input } from '~/components/ui/input'
import { CustomSelect } from '~/components/ui/custom-select'
import { scaleLabel } from '~/components/assessment/labels'
import { ApiError } from '~/api/client'
import {
    createDashboard,
    deleteDashboard,
    listDashboards,
} from '~/api/monitoring'
import type { MonitoringDashboard } from '~/api/types'
import { listAssessments } from '~/api/assessment'
import type { AssessmentResponse } from '~/api/types'
import { AssessmentPicker } from '~/features/monitoring/assessment-picker'

// 빈 화면 단계 안내 — 모니터링이 어떻게 동작하는지 세 단계로 보여준다.
const ONBOARDING_STEPS: { icon: LucideIcon; title: string; desc: string }[] = [
    {
        icon: ClipboardList,
        title: '평가 선택',
        desc: '연결할 인증 평가를 고르면 정량지표(온도·세척 등)가 자동으로 잡혀요.',
    },
    {
        icon: Database,
        title: '데이터 연결',
        desc: 'CCP 일지·IoT·엑셀로 업장 측정값을 자동으로 모읍니다.',
    },
    {
        icon: BellRing,
        title: '이탈 알림',
        desc: '기준을 벗어난 값이 들어오면 이메일·문자로 바로 알려드려요.',
    },
]

/** 모니터링 진입 — 대시보드 선택(우상단 새 대시보드). 준비 중 기능이라 헤더 탭은 숨김. */
export function MonitoringPage() {
    const navigate = useNavigate()
    const [dashboards, setDashboards] = useState<MonitoringDashboard[] | null>(
        null
    )
    const [assessments, setAssessments] = useState<AssessmentResponse[]>([])
    const [showForm, setShowForm] = useState(false)
    const [name, setName] = useState('')
    const [assessmentId, setAssessmentId] = useState('')
    const [creating, setCreating] = useState(false)
    const [error, setError] = useState<string | null>(null)
    // 목록 검색·필터(클라이언트). 데이터 로딩/링크는 그대로 두고 표시만 거른다.
    const [query, setQuery] = useState('')
    const [scaleFilter, setScaleFilter] = useState('all')
    const [classificationFilter, setClassificationFilter] = useState('all')
    const [typeFilter, setTypeFilter] = useState('all')

    const reloadDashboards = () =>
        listDashboards()
            .then(setDashboards)
            .catch(() => setDashboards([]))

    useEffect(() => {
        void reloadDashboards()
        listAssessments()
            .then(setAssessments)
            .catch(() => setAssessments([]))
    }, [])

    const handleDelete = async (d: MonitoringDashboard) => {
        const ok = window.confirm(
            `"${d.name}" 대시보드를 삭제할까요?\n수집된 측정값·매핑·알림도 함께 삭제됩니다.`
        )
        if (!ok) return
        try {
            await deleteDashboard(d.id)
            await reloadDashboards()
        } catch (e: unknown) {
            setError(
                e instanceof ApiError
                    ? e.message
                    : '대시보드를 삭제하지 못했어요.'
            )
        }
    }

    // 대시보드 카드에 연결 평가(규모·분류·평가명)를 보여주기 위한 맵.
    const assessmentById = useMemo(
        () => new Map(assessments.map((a) => [a.id, a])),
        [assessments]
    )

    // 검색(이름·평가명·업종)·필터(규모·분류·단계)를 적용한 표시용 목록.
    const filteredDashboards = useMemo(() => {
        if (!dashboards) return null
        const q = query.trim().toLowerCase()
        return dashboards.filter((d) => {
            const a = d.assessmentId ? assessmentById.get(d.assessmentId) : null

            if (q) {
                const haystack = [d.name, a?.title, a?.industry]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase()
                if (!haystack.includes(q)) return false
            }

            if (scaleFilter !== 'all' && a?.scale !== scaleFilter) return false
            if (
                classificationFilter !== 'all' &&
                a?.classification !== classificationFilter
            )
                return false
            if (typeFilter !== 'all' && a?.assessmentType !== typeFilter)
                return false

            return true
        })
    }, [
        dashboards,
        assessmentById,
        query,
        scaleFilter,
        classificationFilter,
        typeFilter,
    ])

    const handleCreate = async () => {
        if (!name.trim() || creating) return
        setCreating(true)
        setError(null)
        try {
            const created = await createDashboard({
                name: name.trim(),
                assessmentId: assessmentId || null,
            })
            navigate(`/monitoring/${created.id}`)
        } catch (e: unknown) {
            setError(
                e instanceof ApiError
                    ? e.message
                    : '대시보드를 만들지 못했어요.'
            )
            setCreating(false)
        }
    }

    return (
        <AppShell>
            <div className='h-full overflow-y-auto'>
                <div className='mx-auto w-full max-w-[1680px] px-4 py-8 lg:px-8 2xl:px-10'>
                    <div className='mb-6 flex items-end justify-between gap-4'>
                        <div>
                            <h1 className='text-foreground text-lg font-semibold'>
                                모니터링 대시보드
                            </h1>
                            <p className='text-muted-foreground mt-1 text-sm'>
                                업장 체크리스트의 정량지표 준수 현황을 보는
                                대시보드를 선택하거나 새로 만듭니다.
                            </p>
                        </div>
                        <Button onClick={() => setShowForm((v) => !v)}>
                            <Plus />새 대시보드
                        </Button>
                    </div>

                    {error && !showForm ? (
                        <ErrorBanner className='mb-4'>{error}</ErrorBanner>
                    ) : null}

                    <Modal
                        open={showForm}
                        onClose={() => setShowForm(false)}
                        title='새 대시보드'>
                        <div className='grid gap-3'>
                            <div className='grid gap-1.5'>
                                <label className='text-foreground text-sm font-medium'>
                                    대시보드 이름
                                </label>
                                <Input
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder='예: 가열·냉장 공정 모니터링'
                                    autoFocus
                                />
                            </div>
                            <div className='grid gap-1.5'>
                                <label className='text-foreground text-sm font-medium'>
                                    연결할 평가
                                </label>
                                <AssessmentPicker
                                    assessments={assessments}
                                    value={assessmentId}
                                    onChange={setAssessmentId}
                                />
                            </div>
                            {error ? <ErrorBanner>{error}</ErrorBanner> : null}
                            <div className='mt-1 flex justify-end gap-2'>
                                <Button
                                    variant='outline'
                                    onClick={() => setShowForm(false)}>
                                    취소
                                </Button>
                                <Button
                                    onClick={handleCreate}
                                    loading={creating}
                                    disabled={!name.trim()}>
                                    만들기
                                </Button>
                            </div>
                        </div>
                    </Modal>

                    {dashboards && dashboards.length > 0 ? (
                        <div className='mb-5 flex flex-wrap items-center gap-2'>
                            <label className='border-input bg-card focus-within:border-primary focus-within:ring-ring flex h-10 min-w-0 flex-1 items-center gap-2 rounded-md border px-3 transition-colors focus-within:ring-2'>
                                <Search className='text-muted-foreground size-4 shrink-0' />
                                <input
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder='대시보드·평가명·업종 검색'
                                    aria-label='대시보드 검색'
                                    className='text-foreground placeholder:text-muted-foreground w-full bg-transparent text-sm outline-none'
                                />
                            </label>
                            <CustomSelect
                                value={scaleFilter}
                                onValueChange={setScaleFilter}
                                ariaLabel='규모 필터'
                                containerClassName='w-32 shrink-0'
                                options={[
                                    { value: 'all', label: '규모 전체' },
                                    { value: '일반', label: '일반' },
                                    { value: '소규모', label: '소규모' },
                                ]}
                            />
                            <CustomSelect
                                value={classificationFilter}
                                onValueChange={setClassificationFilter}
                                ariaLabel='분류 필터'
                                containerClassName='w-36 shrink-0'
                                options={[
                                    { value: 'all', label: '분류 전체' },
                                    {
                                        value: '선행요건',
                                        label: '선행요건',
                                    },
                                    {
                                        value: 'HACCP관리',
                                        label: 'HACCP관리',
                                    },
                                ]}
                            />
                            <CustomSelect
                                value={typeFilter}
                                onValueChange={setTypeFilter}
                                ariaLabel='단계 필터'
                                containerClassName='w-32 shrink-0'
                                options={[
                                    { value: 'all', label: '단계 전체' },
                                    { value: '인증', label: '인증' },
                                    { value: '정기', label: '정기' },
                                ]}
                            />
                        </div>
                    ) : null}

                    {dashboards === null ? (
                        <p className='text-muted-foreground text-sm'>
                            불러오는 중…
                        </p>
                    ) : dashboards.length === 0 ? (
                        <div className='border-border bg-card rounded-md border p-8 sm:p-12'>
                            <div className='mx-auto flex max-w-xl flex-col items-center text-center'>
                                <div className='bg-accent text-primary grid size-14 place-items-center rounded-full'>
                                    <Activity className='size-7' />
                                </div>
                                <h2 className='text-foreground mt-5 text-base font-semibold'>
                                    아직 모니터링 대시보드가 없어요
                                </h2>
                                <p className='text-muted-foreground mt-2 text-sm leading-6'>
                                    모니터링은 업장의 정량지표를 자동으로 모아
                                    HACCP 기준 준수 현황을 한눈에 보여주고,
                                    기준을 벗어나면 바로 알려드립니다. 평가와
                                    연결해 시작해 보세요.
                                </p>
                                <Button
                                    className='mt-6'
                                    onClick={() => setShowForm(true)}>
                                    <Plus />새 대시보드
                                </Button>
                                <ol className='mt-9 grid w-full gap-3 text-left sm:grid-cols-3'>
                                    {ONBOARDING_STEPS.map((step, i) => {
                                        const Icon = step.icon
                                        return (
                                            <li
                                                key={step.title}
                                                className='border-border bg-background rounded-md border p-4'>
                                                <div className='flex items-center gap-2'>
                                                    <span className='bg-accent text-primary grid size-7 place-items-center rounded-md'>
                                                        <Icon className='size-4' />
                                                    </span>
                                                    <p className='text-foreground text-sm font-medium'>
                                                        <span className='text-muted-foreground tabular-nums'>
                                                            {i + 1}.
                                                        </span>{' '}
                                                        {step.title}
                                                    </p>
                                                </div>
                                                <p className='text-muted-foreground mt-2 text-xs leading-5'>
                                                    {step.desc}
                                                </p>
                                            </li>
                                        )
                                    })}
                                </ol>
                            </div>
                        </div>
                    ) : filteredDashboards &&
                      filteredDashboards.length === 0 ? (
                        <p className='text-muted-foreground text-sm'>
                            조건에 맞는 대시보드가 없어요.
                        </p>
                    ) : (
                        <ul className='grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
                            {(filteredDashboards ?? []).map((d) => {
                                const a = d.assessmentId
                                    ? assessmentById.get(d.assessmentId)
                                    : null
                                return (
                                    <li
                                        key={d.id}
                                        className='group/card relative'>
                                        <button
                                            type='button'
                                            onClick={() => handleDelete(d)}
                                            aria-label={`${d.name} 대시보드 삭제`}
                                            className='text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus-visible:ring-ring absolute right-2 bottom-2 z-10 grid size-7 place-items-center rounded-md opacity-0 transition-colors group-hover/card:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:outline-none'>
                                            <Trash2 className='size-4' />
                                        </button>
                                        <Link
                                            to={`/monitoring/${d.id}`}
                                            className='border-border bg-card hover:border-primary group flex h-full items-start justify-between gap-3 rounded-md border p-5 transition-colors'>
                                            <div className='min-w-0'>
                                                <p className='text-foreground text-sm font-semibold'>
                                                    {d.name}
                                                </p>
                                                {a ? (
                                                    <>
                                                        <div className='mt-2 flex flex-wrap items-center gap-1.5'>
                                                            <Badge tone='info'>
                                                                {scaleLabel(
                                                                    a.scale
                                                                )}
                                                            </Badge>
                                                            {a.classification ? (
                                                                <Badge tone='accent'>
                                                                    {
                                                                        a.classification
                                                                    }
                                                                </Badge>
                                                            ) : null}
                                                            {a.assessmentType ? (
                                                                <Badge tone='neutral'>
                                                                    {
                                                                        a.assessmentType
                                                                    }
                                                                </Badge>
                                                            ) : null}
                                                        </div>
                                                        <p className='text-muted-foreground mt-1.5 truncate text-xs'>
                                                            {a.title}
                                                            {a.industry
                                                                ? ` · ${a.industry}`
                                                                : ''}
                                                        </p>
                                                    </>
                                                ) : (
                                                    <div className='mt-2'>
                                                        <Badge tone='warning'>
                                                            평가 미연결
                                                        </Badge>
                                                    </div>
                                                )}
                                                <p className='text-muted-foreground mt-2 text-xs'>
                                                    측정값{' '}
                                                    {(
                                                        d.readingCount ?? 0
                                                    ).toLocaleString()}
                                                    건
                                                    {d.createdAt
                                                        ? ` · 생성 ${d.createdAt.slice(0, 10)}`
                                                        : ''}
                                                </p>
                                            </div>
                                            <ArrowRight className='text-muted-foreground group-hover:text-primary mt-1 size-4 shrink-0' />
                                        </Link>
                                    </li>
                                )
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </AppShell>
    )
}
